import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "../../lib/cn";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

export function Modal({
  open,
  onClose,
  title,
  description,
  eyebrow,
  headerContent,
  children,
  footer,
  className,
  panelClassName,
  titleId = "modal-title",
}) {
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50"
      onClick={onClose}
      role="presentation"
    >
      <div className="absolute inset-0 bg-black/50" aria-hidden />
      <div className="relative z-10 flex h-full items-center justify-center p-4 overflow-y-auto">
        <div
          className={cn(
            "flex max-h-[min(90vh,720px)] w-full max-w-lg flex-col rounded-xl border border-border bg-card shadow-lg",
            panelClassName,
          )}
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-5 pb-4 pt-5">
            <div className="min-w-0">
              {eyebrow ? (
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {eyebrow}
                </p>
              ) : null}
              <h2
                id={titleId}
                className={cn(
                  "font-semibold leading-tight text-foreground",
                  eyebrow ? "mt-1 text-xl" : "text-xl",
                )}
              >
                {title}
              </h2>
              {description ? (
                <p className="mt-1 text-sm text-muted-foreground">{description}</p>
              ) : null}
              {headerContent ? <div className="mt-2">{headerContent}</div> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className={cn("overflow-y-auto px-5 py-4", className)}>
            {children}
          </div>

          {footer ? (
            <div className="border-t border-border bg-muted/20 px-5 py-3">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
